
// client/src/components/event/EventActionDetail.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./EventActionDetail.css";
import { useTranslation } from "react-i18next";
import PerformancePanel from "../analytics/PerformancePanel";

const ACTION_META = {
  auction:          { label: "Auction",            icon: "\u{1F528}", entriesLabel: "Bids",        entryNoun: "bid" },
  raffle:           { label: "Raffle",             icon: "\u{1F3AB}", entriesLabel: "Entries",      entryNoun: "entry" },
  challenge:        { label: "Challenge",          icon: "\u{1F3C6}", entriesLabel: "Submissions",  entryNoun: "submission" },
  contest:          { label: "Contest",            icon: "\u{1F3C5}", entriesLabel: "Entries",      entryNoun: "entry" },
  ama:              { label: "AMA",                icon: "\u{1F4AC}", entriesLabel: "Questions",    entryNoun: "question" },
  ugc_campaign:     { label: "UGC Campaign",       icon: "\u{1F4F7}", entriesLabel: "Submissions",  entryNoun: "submission" },
  themed_day:       { label: "Themed Day",         icon: "\u{1F389}", entriesLabel: "Posts",        entryNoun: "post" },
  collab_takeover:  { label: "Collab / Takeover",  icon: "\u{1F91D}", entriesLabel: "Details",      entryNoun: "detail" },
  poll_quiz:        { label: "Poll / Quiz",        icon: "\u{1F4CA}", entriesLabel: "Responses",    entryNoun: "response" },
};

const INBOX_TYPES = ["ama", "ugc_campaign", "challenge", "contest", "themed_day"];
const WINNERS_TYPES = ["auction", "raffle", "challenge", "contest"];

const EventActionDetail = () => {
  const { tenant: tenantId, eventId, itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const actionType = queryParams.get("type") || "raffle";

  const { t: tr } = useTranslation();
  const meta = ACTION_META[actionType] || ACTION_META.raffle;
  const isInboxType = INBOX_TYPES.includes(actionType);
  const hasWinners = WINNERS_TYPES.includes(actionType);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [config, setConfig] = useState(null);
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("entries");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [pollAggregates, setPollAggregates] = useState([]);
  const [hashtagPosts, setHashtagPosts] = useState([]);
  const [hashtagLoading, setHashtagLoading] = useState(false);
  const [amaQuestionText, setAmaQuestionText] = useState("");
  const [itemMedia, setItemMedia] = useState([]);

  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  const flash = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  }, []);

  // ── Data Loading ──────────────────────────────────────────

  const configEndpoint = useMemo(() => {
    const map = {
      auction: `/api/auction/${tenantId}/event/${eventId}/item/${itemId}`,
      raffle:  `/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`,
    };
    const slug = actionType.replace(/_/g, "-");
    return map[actionType] || `/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/config`;
  }, [actionType, tenantId, eventId, itemId]);

  const entriesEndpoint = useMemo(() => {
    const slug = actionType.replace(/_/g, "-");
    const suffixMap = {
      auction: "entries", raffle: "entries", challenge: "entries", contest: "entries",
      ama: "questions", poll_quiz: "responses", ugc_campaign: "submissions", themed_day: "posts",
    };
    const suffix = suffixMap[actionType];
    if (!suffix) return null;
    if (actionType === "auction" || actionType === "raffle") {
      return `/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/${suffix}`;
    }
    return `/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/${suffix}`;
  }, [actionType, tenantId, eventId, itemId]);

  const winnersEndpoint = useMemo(() => {
    if (!hasWinners) return null;
    const slug = actionType.replace(/_/g, "-");
    return `/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/winners`;
  }, [actionType, tenantId, eventId, itemId, hasWinners]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const requests = [axios.get(`${baseUrl}${configEndpoint}`)];
        if (entriesEndpoint) requests.push(axios.get(`${baseUrl}${entriesEndpoint}`));
        if (winnersEndpoint) requests.push(axios.get(`${baseUrl}${winnersEndpoint}`));

        const results = await Promise.all(requests.map(r => r.catch(e => e)));

        // Config
        const cfgRes = results[0];
        if (cfgRes?.data?.success !== false) {
          const key = actionType === "auction" ? "auction" : actionType === "raffle" ? "raffle" : actionType;
          setConfig(cfgRes.data?.[key] || cfgRes.data || null);
        }

        // Entries
        if (entriesEndpoint && results[1]?.data) {
          const d = results[1].data;
          const items = d.entries || d.questions || d.responses || d.submissions || d.posts || d.registrations || [];
          setEntries(items);

          // Build poll aggregates
          if (actionType === "poll_quiz" && items.length > 0) {
            buildPollAggregates(cfgRes.data?.poll_quiz || cfgRes.data, items);
          }
        }

        // Winners
        if (winnersEndpoint && results.length > 2 && results[2]?.data) {
          setWinners(results[2].data?.winners || []);
        }
      } catch (err) {
        console.error("[EventActionDetail] Load error:", err);
        flash("Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [baseUrl, configEndpoint, entriesEndpoint, winnersEndpoint, actionType]);

  // Load hashtag posts for challenges
  useEffect(() => {
    if (actionType !== "challenge" || !config?.hashtag) return;
    const loadHashtagPosts = async () => {
      setHashtagLoading(true);
      try {
        const tag = config.hashtag.replace(/^#/, "");
        const resp = await axios.get(`${baseUrl}/api/events/${tenantId}/posts/hashtag/${encodeURIComponent(tag)}`);
        if (resp.data.success) setHashtagPosts(resp.data.posts || []);
      } catch (err) {
        console.error("[EventActionDetail] Hashtag search error:", err);
      } finally {
        setHashtagLoading(false);
      }
    };
    loadHashtagPosts();
  }, [actionType, config?.hashtag, baseUrl, tenantId]);

  // Load item media for AMA post overlay
  useEffect(() => {
    if (actionType !== "ama") return;
    const loadMedia = async () => {
      try {
        const resp = await axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/images`);
        const images = resp.data?.images || [];
        setItemMedia(images.map(img => {
          const path = img.path || img.url || img;
          return /^https?:\/\//i.test(path) ? path : `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
        }));
      } catch (err) {
        console.error("[EventActionDetail] Media load error:", err);
      }
    };
    loadMedia();
  }, [actionType, baseUrl, tenantId, eventId, itemId]);

  // ── Poll Aggregation ──────────────────────────────────────

  const buildPollAggregates = (cfg, responses) => {
    let questions = cfg?.questions;
    if (typeof questions === "string") {
      try { questions = JSON.parse(questions); } catch { questions = []; }
    }
    if (!Array.isArray(questions)) return;

    const agg = questions.map((q, qi) => {
      const optionCounts = (q.options || []).map(() => 0);
      let total = 0;
      responses.forEach(r => {
        let resp = r.responses;
        if (typeof resp === "string") { try { resp = JSON.parse(resp); } catch { resp = {}; } }
        if (resp && resp[qi] !== undefined) {
          const idx = Number(resp[qi]);
          if (optionCounts[idx] !== undefined) { optionCounts[idx]++; total++; }
        }
      });
      return {
        question: q.question || `Question ${qi + 1}`,
        options: (q.options || []).map((opt, oi) => ({
          label: opt,
          count: optionCounts[oi],
          pct: total > 0 ? Math.round((optionCounts[oi] / total) * 100) : 0,
          isCorrect: q.correct_answer !== undefined && Number(q.correct_answer) === oi,
        })),
        total,
      };
    });
    setPollAggregates(agg);
  };

  // ── Helpers ───────────────────────────────────────────────

  const getStatus = () => {
    if (!config) return "draft";
    if (config.status === "COMPLETE" || config.status === "CANCELLED") return "ended";
    if (config.hasEnded) return "ended";
    if (config.hasStarted || config.status === "ACTIVE" || config.status === "LIVE") return "active";
    return "pending";
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const shortDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    const now = new Date();
    if (dt.toDateString() === now.toDateString()) {
      return dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const initial = (name) => (name || "?").charAt(0).toUpperCase();

  // ── Filtering & Sorting ───────────────────────────────────

  const filteredEntries = useMemo(() => {
    let items = [...entries];

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(e =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.question || "").toLowerCase().includes(q) ||
        (e.submission_text || "").toLowerCase().includes(q) ||
        (e.content_text || "").toLowerCase().includes(q) ||
        (e.post_text || "").toLowerCase().includes(q)
      );
    }

    // Filter by tab sub-filter (AMA: answered/unanswered)
    if (actionType === "ama") {
      if (sortBy === "answered") items = items.filter(e => e.answered);
      if (sortBy === "unanswered") items = items.filter(e => !e.answered);
    }

    // Sort
    if (sortBy === "newest" || sortBy === "unanswered") {
      items.sort((a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0));
    } else if (sortBy === "oldest") {
      items.sort((a, b) => new Date(a.created_on || 0) - new Date(b.created_on || 0));
    } else if (sortBy === "upvotes") {
      items.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
    } else if (sortBy === "highest") {
      items.sort((a, b) => Number(b.bidamount || 0) - Number(a.bidamount || 0));
    } else if (sortBy === "answered") {
      // already filtered above, sort by newest
      items.sort((a, b) => new Date(b.answered_at || b.created_on || 0) - new Date(a.answered_at || a.created_on || 0));
    }

    return items;
  }, [entries, searchQuery, sortBy, actionType]);

  const selectedItem = useMemo(() => {
    return entries.find(e => e.id === selectedId) || null;
  }, [entries, selectedId]);

  // ── Actions ───────────────────────────────────────────────

  const handleAnswer = async () => {
    if (!answerText.trim() || !selectedId) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/question/${selectedId}/answer`,
        { answer: answerText.trim() }
      );
      if (resp.data.success) {
        setEntries(prev => prev.map(e =>
          e.id === selectedId ? { ...e, answered: true, answer: answerText.trim(), answered_at: new Date().toISOString() } : e
        ));
        setAnswerText("");
        flash("Answer published", "success");
      }
    } catch (err) {
      flash(err.response?.data?.message || "Failed to submit answer", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeature = async (entryId) => {
    const slug = actionType.replace(/_/g, "-");
    try {
      const resp = await axios.post(`${baseUrl}/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/feature/${entryId}`);
      if (resp.data.success) {
        setEntries(prev => prev.map(e =>
          e.id === entryId ? { ...e, featured: true, featured_at: new Date().toISOString() } : e
        ));
        flash("Content featured", "success");
      }
    } catch (err) {
      flash("Failed to feature content", "error");
    }
  };

  const handleAward = async () => {
    if (!window.confirm(`Award winners for this ${meta.label.toLowerCase()}?`)) return;
    const slug = actionType.replace(/_/g, "-");
    try {
      const resp = await axios.post(`${baseUrl}/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/award`);
      if (resp.data.success) {
        setWinners(resp.data.winners || []);
        flash("Winners awarded", "success");
        setActiveTab("winners");
      }
    } catch (err) {
      flash(err.response?.data?.message || "Failed to award winners", "error");
    }
  };

  const handleDraw = async () => {
    if (!window.confirm("Draw raffle winners now?")) return;
    try {
      const resp = await axios.post(`${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/draw`);
      if (resp.data.success) {
        setWinners(resp.data.winners || []);
        flash("Winners drawn", "success");
        setActiveTab("winners");
      }
    } catch (err) {
      flash(err.response?.data?.message || "Failed to draw winners", "error");
    }
  };

  const handleNotify = async () => {
    if (!window.confirm("Send winner notifications?")) return;
    const slug = actionType.replace(/_/g, "-");
    try {
      await axios.post(`${baseUrl}/api/${slug}/${tenantId}/event/${eventId}/item/${itemId}/notify`);
      flash("Notifications sent", "success");
    } catch (err) {
      flash("Failed to send notifications", "error");
    }
  };

  const selectItem = (id) => {
    setSelectedId(id);
    setAnswerText("");
    setMobileDetailOpen(true);
  };

  // ── Stats ─────────────────────────────────────────────────

  const stats = useMemo(() => {
    const s = [];
    s.push({ label: meta.entriesLabel, value: entries.length });

    if (actionType === "auction") {
      const bids = entries.map(e => Number(e.bidamount || 0));
      if (bids.length) s.push({ label: "Highest", value: `$${Math.max(...bids).toFixed(2)}` });
    }
    if (hasWinners) s.push({ label: "Winners", value: winners.length });
    if (actionType === "ama") {
      s.push({ label: "Answered", value: entries.filter(e => e.answered).length });
    }
    if (actionType === "ugc_campaign" || actionType === "themed_day") {
      s.push({ label: "Featured", value: entries.filter(e => e.featured).length });
    }
    if (actionType === "poll_quiz") {
      s.push({ label: "Avg Score", value: entries.length ? (entries.reduce((a, e) => a + (Number(e.score) || 0), 0) / entries.length).toFixed(1) : "-" });
    }
    return s;
  }, [entries, winners, actionType, meta, hasWinners]);

  // ── Render Helpers ────────────────────────────────────────

  const getItemPreview = (item) => {
    if (actionType === "auction") return `$${Number(item.bidamount || 0).toFixed(2)}`;
    if (actionType === "ama") return item.question || "(no question)";
    if (actionType === "challenge") return item.submission_text || item.submission_url || "(no submission)";
    if (actionType === "contest") return item.email || "";
    if (actionType === "ugc_campaign") return item.content_text || item.content_url || "(no content)";
    if (actionType === "themed_day") return item.post_text || item.post_url || "(no post)";
    if (actionType === "poll_quiz") return `Score: ${item.score ?? "-"}`;
    return item.email || "";
  };

  const getItemTag = (item) => {
    if (actionType === "auction") return { cls: "bid", text: `$${Number(item.bidamount || 0).toFixed(2)}` };
    if (actionType === "ama") return item.answered ? { cls: "answered", text: "Answered" } : { cls: "question", text: "Pending" };
    if (actionType === "ugc_campaign" || actionType === "themed_day") return item.featured ? { cls: "featured", text: "Featured" } : { cls: "submission", text: "New" };
    if (actionType === "challenge") return { cls: "submission", text: "Submission" };
    return { cls: "entry", text: "Entry" };
  };

  // ── Render: Inbox List Item ───────────────────────────────

  const renderListItem = (item) => {
    const tag = getItemTag(item);
    return (
      <div
        key={item.id}
        className={`ead-inbox-item${selectedId === item.id ? " selected" : ""}`}
        onClick={() => selectItem(item.id)}
      >
        <div className="ead-inbox-avatar">{initial(item.name)}</div>
        <div className="ead-inbox-item-body">
          <div className="ead-inbox-item-top">
            <span className="ead-inbox-item-name">{item.name || "Anonymous"}</span>
            <span className="ead-inbox-item-time">{shortDate(item.created_on)}</span>
          </div>
          <div className="ead-inbox-item-preview">{getItemPreview(item)}</div>
          <div className="ead-inbox-item-meta">
            <span className={`ead-tag ${tag.cls}`}>{tag.text}</span>
            {item.upvotes > 0 && <span className="ead-upvote-badge">{"\u25B2"} {item.upvotes}</span>}
            {item.platform && <span className="ead-tag entry">{item.platform}</span>}
          </div>
        </div>
      </div>
    );
  };

  // ── Render: Detail Panel ──────────────────────────────────

  const renderDetailPanel = () => {
    if (!selectedItem) {
      return <div className="ead-detail-empty">Select an item to view details</div>;
    }

    const item = selectedItem;

    return (
      <>
        <div className="ead-detail-header">
          <div style={{ display: "flex", alignItems: "center" }}>
            <button className="ead-detail-mobile-back" onClick={() => setMobileDetailOpen(false)}>{"\u2190"} Back</button>
            <div className="ead-detail-header-info">
              <h3 className="ead-detail-name">{item.name || "Anonymous"}</h3>
              <div className="ead-detail-email">{item.email} {item.phone ? `\u00B7 ${item.phone}` : ""}</div>
            </div>
          </div>
          <div className="ead-detail-actions">
            {actionType === "ama" && !item.answered && (
              <span className="ead-tag question" style={{ padding: "4px 10px" }}>Awaiting Answer</span>
            )}
            {actionType === "ama" && item.answered && (
              <span className="ead-tag answered" style={{ padding: "4px 10px" }}>Answered</span>
            )}
            {(actionType === "ugc_campaign" || actionType === "themed_day") && (
              item.featured
                ? <span className="ead-action-btn featured">Featured</span>
                : <button className="ead-action-btn primary" onClick={() => handleFeature(item.id)}>Feature</button>
            )}
          </div>
        </div>

        <div className="ead-detail-body">
          {/* AMA: Conversation-style Q&A */}
          {actionType === "ama" && (
            <div className="ead-conversation">
              <div className="ead-bubble question">
                {item.question || "(registered without a question)"}
                <div className="ead-bubble-meta">{item.name} &middot; {shortDate(item.created_on)} {item.upvotes > 0 && `\u00B7 ${"\u25B2"} ${item.upvotes}`}</div>
              </div>
              {item.answered && item.answer && (
                <div className="ead-bubble answer">
                  {item.answer}
                  <div className="ead-bubble-meta">Host &middot; {shortDate(item.answered_at)}</div>
                </div>
              )}
            </div>
          )}

          {/* Auction bid detail */}
          {actionType === "auction" && (
            <>
              <div className="ead-detail-card">
                <div className="ead-detail-card-label">Bid Amount</div>
                <div className="ead-detail-card-value" style={{ fontSize: "1.5rem", color: "#ff6800", fontWeight: 700 }}>
                  ${Number(item.bidamount || 0).toFixed(2)}
                </div>
              </div>
              {item.attributes && Object.keys(item.attributes).length > 0 && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Selections</div>
                  <div className="ead-detail-card-value">
                    {Object.entries(typeof item.attributes === "string" ? JSON.parse(item.attributes) : item.attributes).map(([k, v]) => (
                      <div key={k}><strong>{k}:</strong> {v}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Raffle entry detail */}
          {actionType === "raffle" && (
            <>
              {item.attributes && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Selections</div>
                  <div className="ead-detail-card-value">
                    {Object.entries(typeof item.attributes === "string" ? JSON.parse(item.attributes) : (item.attributes || {})).map(([k, v]) => (
                      <div key={k}><strong>{k}:</strong> {v}</div>
                    ))}
                  </div>
                </div>
              )}
              <div className="ead-detail-card">
                <div className="ead-detail-card-label">Entry Date</div>
                <div className="ead-detail-card-value">{formatDate(item.created_on)}</div>
              </div>
            </>
          )}

          {/* Challenge submission detail */}
          {actionType === "challenge" && (
            <>
              {item.submission_url && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Submission Link</div>
                  <div className="ead-detail-card-value">
                    <a href={item.submission_url} target="_blank" rel="noopener noreferrer">{item.submission_url}</a>
                  </div>
                </div>
              )}
              {item.submission_text && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Submission Text</div>
                  <div className="ead-detail-card-value">{item.submission_text}</div>
                </div>
              )}
            </>
          )}

          {/* Contest entry detail */}
          {actionType === "contest" && (
            <>
              {item.actions_completed && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Actions Completed</div>
                  <div className="ead-detail-card-value">
                    {(() => {
                      let actions = item.actions_completed;
                      if (typeof actions === "string") { try { actions = JSON.parse(actions); } catch { return actions; } }
                      if (typeof actions === "object") {
                        return Object.entries(actions).map(([k, v]) => (
                          <div key={k}>{k}: {v ? "\u2705" : "\u274C"}</div>
                        ));
                      }
                      return String(actions);
                    })()}
                  </div>
                </div>
              )}
              <div className="ead-detail-card">
                <div className="ead-detail-card-label">Entered</div>
                <div className="ead-detail-card-value">{formatDate(item.created_on)}</div>
              </div>
            </>
          )}

          {/* UGC Campaign submission detail */}
          {actionType === "ugc_campaign" && (
            <>
              {item.content_url && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Content Link</div>
                  <div className="ead-detail-card-value">
                    <a href={item.content_url} target="_blank" rel="noopener noreferrer">{item.content_url}</a>
                  </div>
                </div>
              )}
              {item.content_text && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Description</div>
                  <div className="ead-detail-card-value">{item.content_text}</div>
                </div>
              )}
              {item.platform && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Platform</div>
                  <div className="ead-detail-card-value">{item.platform}</div>
                </div>
              )}
            </>
          )}

          {/* Themed Day post detail */}
          {actionType === "themed_day" && (
            <>
              {item.post_url && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Post Link</div>
                  <div className="ead-detail-card-value">
                    <a href={item.post_url} target="_blank" rel="noopener noreferrer">{item.post_url}</a>
                  </div>
                </div>
              )}
              {item.post_text && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Post Text</div>
                  <div className="ead-detail-card-value">{item.post_text}</div>
                </div>
              )}
              {item.platform && (
                <div className="ead-detail-card">
                  <div className="ead-detail-card-label">Platform</div>
                  <div className="ead-detail-card-value">{item.platform}</div>
                </div>
              )}
            </>
          )}

          {/* Common: Date submitted */}
          {actionType !== "raffle" && actionType !== "contest" && (
            <div className="ead-detail-card">
              <div className="ead-detail-card-label">Submitted</div>
              <div className="ead-detail-card-value">{formatDate(item.created_on)}</div>
            </div>
          )}
        </div>

        {/* AMA answer compose */}
        {actionType === "ama" && !selectedItem?.answered && (
          <div className="ead-compose">
            <div className="ead-compose-label">Your Answer</div>
            <div className="ead-compose-row">
              <textarea
                className="ead-compose-input"
                placeholder="Type your answer..."
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                rows={2}
                disabled={submitting}
              />
              <button
                className="ead-compose-send"
                onClick={handleAnswer}
                disabled={submitting || !answerText.trim()}
              >
                {submitting ? "Sending..." : "Publish"}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ── Render: Table View (Auction/Raffle) ───────────────────

  const renderTableView = () => {
    if (actionType === "auction") {
      return (
        <div className="ead-table-view">
          <div className="ead-table-wrapper">
            <table className="ead-table">
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Email</th><th>Bid</th><th>Selections</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e, i) => (
                  <tr key={e.id} onClick={() => selectItem(e.id)} style={{ cursor: "pointer" }}>
                    <td>{i + 1}</td>
                    <td>{e.name}</td>
                    <td>{e.email}</td>
                    <td className="highlight">${Number(e.bidamount || 0).toFixed(2)}</td>
                    <td>{(() => {
                      const a = typeof e.attributes === "string" ? (() => { try { return JSON.parse(e.attributes); } catch { return null; } })() : e.attributes;
                      return a ? Object.entries(a).map(([k, v]) => `${k}: ${v}`).join(", ") : "-";
                    })()}</td>
                    <td>{shortDate(e.created_on)}</td>
                  </tr>
                ))}
                {filteredEntries.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: "center", color: "#556", padding: 32 }}>No bids yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Raffle
    return (
      <div className="ead-table-view">
        <div className="ead-table-wrapper">
          <table className="ead-table">
            <thead>
              <tr>
                <th>#</th><th>Name</th><th>Email</th><th>Selections</th><th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e, i) => (
                <tr key={e.id}>
                  <td>{i + 1}</td>
                  <td>{e.name}</td>
                  <td>{e.email}</td>
                  <td>{(() => {
                    const a = typeof e.attributes === "string" ? (() => { try { return JSON.parse(e.attributes); } catch { return null; } })() : e.attributes;
                    return a ? Object.entries(a).map(([k, v]) => `${k}: ${v}`).join(", ") : "-";
                  })()}</td>
                  <td>{shortDate(e.created_on)}</td>
                </tr>
              ))}
              {filteredEntries.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "#556", padding: 32 }}>No entries yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render: Winners Table ─────────────────────────────────

  const renderWinnersTable = () => (
    <div className="ead-table-view">
      <div className="ead-table-wrapper">
        <table className="ead-table">
          <thead>
            <tr>
              <th>Place</th><th>Name</th><th>Email</th>
              {actionType === "auction" && <th>Bid</th>}
              <th>Notified</th>
            </tr>
          </thead>
          <tbody>
            {winners.map((w, i) => (
              <tr key={w.id || i}>
                <td style={{ fontWeight: 700, fontSize: 16 }}>{i < 3 ? ["\u{1F947}", "\u{1F948}", "\u{1F949}"][i] : i + 1}</td>
                <td>{w.name}</td>
                <td>{w.email}</td>
                {actionType === "auction" && <td className="highlight">${Number(w.bidamount || 0).toFixed(2)}</td>}
                <td>{w.winner_notified ? "\u2705" : "\u23F3"}</td>
              </tr>
            ))}
            {winners.length === 0 && (
              <tr><td colSpan={actionType === "auction" ? 5 : 4} style={{ textAlign: "center", color: "#556", padding: 32 }}>No winners yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Render: Poll/Quiz Results ─────────────────────────────

  const renderPollResults = () => (
    <div className="ead-poll-results">
      {pollAggregates.length === 0 && (
        <div className="ead-empty">
          <div className="ead-empty-icon">{"\u{1F4CA}"}</div>
          <div className="ead-empty-text">No responses yet</div>
        </div>
      )}
      {pollAggregates.map((q, qi) => (
        <div key={qi} className="ead-poll-question">
          <div className="ead-poll-question-text">{qi + 1}. {q.question}</div>
          {q.options.map((opt, oi) => (
            <div key={oi} className="ead-poll-option">
              <div className="ead-poll-option-row">
                <span className="ead-poll-option-label">
                  {opt.label} {opt.isCorrect && "\u2705"}
                </span>
                <span className="ead-poll-option-pct">{opt.pct}% ({opt.count})</span>
              </div>
              <div className="ead-poll-bar">
                <div
                  className={`ead-poll-bar-fill${opt.isCorrect ? " correct" : ""}`}
                  style={{ width: `${opt.pct}%` }}
                />
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#556", marginTop: 8 }}>{q.total} total responses</div>
        </div>
      ))}
    </div>
  );

  // ── Render: Collab/Takeover Summary ───────────────────────

  const renderCollabSummary = () => (
    <div className="ead-summary">
      <div className="ead-summary-grid">
        {config?.description && (
          <div className="ead-summary-card" style={{ gridColumn: "1 / -1" }}>
            <div className="ead-summary-card-label">Description</div>
            <div className="ead-summary-card-value">{config.description}</div>
          </div>
        )}
        {config?.partner_name && (
          <div className="ead-summary-card">
            <div className="ead-summary-card-label">Partner</div>
            <div className="ead-summary-card-value">{config.partner_name}</div>
          </div>
        )}
        {config?.partner_contact && (
          <div className="ead-summary-card">
            <div className="ead-summary-card-label">Contact</div>
            <div className="ead-summary-card-value">{config.partner_contact}</div>
          </div>
        )}
        {config?.type && (
          <div className="ead-summary-card">
            <div className="ead-summary-card-label">Type</div>
            <div className="ead-summary-card-value">{config.type === "TAKEOVER" ? "Takeover" : "Collaboration"}</div>
          </div>
        )}
        {config?.scheduled_at && (
          <div className="ead-summary-card">
            <div className="ead-summary-card-label">Scheduled</div>
            <div className="ead-summary-card-value">{formatDate(config.scheduled_at)}</div>
          </div>
        )}
        {config?.duration_hours && (
          <div className="ead-summary-card">
            <div className="ead-summary-card-label">Duration</div>
            <div className="ead-summary-card-value">{config.duration_hours} hours</div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Render: Challenge Hashtag Posts ────────────────────────

  const renderHashtagPosts = () => (
    <div className="ead-table-view">
      {hashtagLoading && <div className="ead-loading">Searching posts with {config?.hashtag}...</div>}
      {!hashtagLoading && hashtagPosts.length === 0 && (
        <div className="ead-empty">
          <div className="ead-empty-icon">#</div>
          <div className="ead-empty-text">No posts found with {config?.hashtag}</div>
        </div>
      )}
      {!hashtagLoading && hashtagPosts.length > 0 && (
        <div className="ead-hashtag-feed">
          {hashtagPosts.map(post => (
            <div key={post.id} className="ead-hashtag-card">
              {/* Media preview */}
              {post.media_urls?.length > 0 && (
                <div className="ead-hashtag-media">
                  <img
                    src={post.media_urls[0]}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              )}
              <div className="ead-hashtag-body">
                <p className="ead-hashtag-content">{post.content}</p>
                <div className="ead-hashtag-metrics">
                  {post.likes != null && (
                    <span className="ead-metric"><span className="ead-metric-icon">{"\u2764"}</span> {post.likes}</span>
                  )}
                  {post.comments != null && (
                    <span className="ead-metric"><span className="ead-metric-icon">{"\u{1F4AC}"}</span> {post.comments}</span>
                  )}
                  {post.shares != null && (
                    <span className="ead-metric"><span className="ead-metric-icon">{"\u{1F504}"}</span> {post.shares}</span>
                  )}
                  {post.impressions != null && (
                    <span className="ead-metric"><span className="ead-metric-icon">{"\u{1F441}"}</span> {post.impressions}</span>
                  )}
                </div>
                <div className="ead-hashtag-footer">
                  <span>{(post.platforms || []).join(", ")}</span>
                  <span>{post.posted_on ? new Date(post.posted_on).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Render: AMA Post Overlay with Question Input ──────────

  const renderAmaOverlay = () => {
    const heroImage = itemMedia.length > 0 ? itemMedia[0] : null;

    return (
      <div className="ead-ama-overlay">
        {/* Post image background */}
        <div className="ead-ama-post-area">
          {heroImage ? (
            <img className="ead-ama-post-image" src={heroImage} alt="AMA post" />
          ) : (
            <div className="ead-ama-post-placeholder">
              <div style={{ fontSize: 48, opacity: 0.3 }}>{"\u{1F4AC}"}</div>
              <div style={{ color: "#8899b0", fontSize: 13 }}>AMA Post</div>
            </div>
          )}

          {/* Floating question sticker overlay */}
          <div className="ead-ama-sticker">
            <div className="ead-ama-sticker-header">
              <span className="ead-ama-sticker-label">Ask me anything</span>
              {config?.host_name && (
                <span className="ead-ama-sticker-host">with {config.host_name}</span>
              )}
            </div>
            <div className="ead-ama-sticker-input-row">
              <input
                className="ead-ama-sticker-input"
                type="text"
                placeholder="Type a question..."
                value={amaQuestionText}
                onChange={(e) => setAmaQuestionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && amaQuestionText.trim()) handleAmaSubmitFromOverlay();
                }}
                disabled={submitting}
              />
              <button
                className="ead-ama-sticker-send"
                onClick={handleAmaSubmitFromOverlay}
                disabled={submitting || !amaQuestionText.trim()}
              >
                {submitting ? "..." : "\u{27A4}"}
              </button>
            </div>
          </div>
        </div>

        {/* Quick stats beneath */}
        <div className="ead-ama-overlay-stats">
          <span>{entries.length} question{entries.length !== 1 ? "s" : ""}</span>
          <span>{entries.filter(e => e.answered).length} answered</span>
          {config?.scheduled_at && (
            <span>{new Date(config.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          )}
        </div>
      </div>
    );
  };

  const handleAmaSubmitFromOverlay = async () => {
    if (!amaQuestionText.trim()) return;
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/question`,
        { name: "Admin", email: "admin@tenant.com", question: amaQuestionText.trim() }
      );
      if (resp.data.success) {
        flash("Question submitted", "success");
        setAmaQuestionText("");
        // Refresh questions
        const qResp = await axios.get(`${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/questions`);
        if (qResp.data.success) setEntries(qResp.data.questions || []);
      }
    } catch (err) {
      flash(err.response?.data?.message || "Failed to submit question", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Tabs Config ───────────────────────────────────────────

  const tabs = useMemo(() => {
    const t = [];
    if (actionType === "collab_takeover") {
      t.push({ key: "details", label: "Details" });
    } else if (actionType === "poll_quiz") {
      t.push({ key: "results", label: "Results", count: entries.length });
      t.push({ key: "entries", label: "Responses", count: entries.length });
    } else {
      t.push({ key: "entries", label: meta.entriesLabel, count: entries.length });
    }
    if (actionType === "challenge" && config?.hashtag) {
      t.push({ key: "hashtag_posts", label: `${config.hashtag} Posts`, count: hashtagPosts.length });
    }
    if (hasWinners) {
      t.push({ key: "winners", label: "Winners", count: winners.length });
      // Raffle / auction / contest / challenge: tracked-link clicks → entries
      t.push({ key: "performance", label: tr("performance.title", "Performance") });
    }
    return t;
  }, [actionType, entries.length, winners.length, meta.entriesLabel, hasWinners, config?.hashtag, hashtagPosts.length, tr]);

  // ── Sort Options ──────────────────────────────────────────

  const sortOptions = useMemo(() => {
    if (actionType === "ama") return [
      { key: "newest", label: "New" },
      { key: "upvotes", label: "Top" },
      { key: "answered", label: "Answered" },
      { key: "unanswered", label: "Pending" },
    ];
    if (actionType === "auction") return [
      { key: "highest", label: "Highest" },
      { key: "newest", label: "Newest" },
    ];
    return [
      { key: "newest", label: "Newest" },
      { key: "oldest", label: "Oldest" },
    ];
  }, [actionType]);

  // ── Main Render ───────────────────────────────────────────

  if (loading) {
    return <div className="ead-container"><div className="ead-loading">Loading {meta.label} details...</div></div>;
  }

  if (!config) {
    return (
      <div className="ead-container">
        <div className="ead-header">
          <button className="ead-back-btn" onClick={() => navigate(-1)}>{"\u2190"}</button>
          <div className="ead-header-info">
            <h1>{meta.label}</h1>
          </div>
        </div>
        <div className="ead-empty">
          <div className="ead-empty-icon">{meta.icon}</div>
          <div className="ead-empty-text">No {meta.label.toLowerCase()} configured for this item yet.</div>
        </div>
      </div>
    );
  }

  const status = getStatus();

  return (
    <div className="ead-container">
      {/* Header */}
      <div className="ead-header">
        <button className="ead-back-btn" onClick={() => navigate(-1)}>{"\u2190"}</button>
        <div className="ead-header-info">
          <h1>{meta.icon} {config?.name || meta.label}</h1>
          <div className="ead-header-sub">
            {config?.start_at || config?.scheduled_at
              ? `${formatDate(config.start_at || config.scheduled_at)}${config.end_at ? ` \u2013 ${formatDate(config.end_at)}` : ""}`
              : meta.label
            }
          </div>
        </div>
        <span className={`ead-status-badge ${status}`}>
          {status === "active" ? "Active" : status === "ended" ? "Ended" : status === "pending" ? "Upcoming" : "Draft"}
        </span>
      </div>

      {/* Message */}
      {message.text && <div className={`ead-message ${message.type}`}>{message.text}</div>}

      {/* Stats Bar */}
      <div className="ead-stats-bar">
        {stats.map((s, i) => (
          <div key={i} className="ead-stat">
            <div className="ead-stat-value">{s.value}</div>
            <div className="ead-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="ead-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`ead-tab${activeTab === t.key ? " active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {t.count !== undefined && <span className="ead-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* AMA Post Overlay - shown above tabs content */}
      {actionType === "ama" && renderAmaOverlay()}

      {/* Content */}
      {activeTab === "details" && renderCollabSummary()}

      {activeTab === "results" && actionType === "poll_quiz" && renderPollResults()}

      {activeTab === "hashtag_posts" && actionType === "challenge" && renderHashtagPosts()}

      {activeTab === "winners" && renderWinnersTable()}
      {activeTab === "performance" && hasWinners && (
        <PerformancePanel tenantId={tenantId} eventId={eventId} itemId={itemId} />
      )}

      {activeTab === "entries" && isInboxType && (
        <div className={`ead-inbox${mobileDetailOpen ? " detail-open" : ""}`}>
          <div className="ead-inbox-list">
            <div className="ead-inbox-toolbar">
              <input
                className="ead-inbox-search"
                placeholder={`Search ${meta.entriesLabel.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {sortOptions.map(opt => (
                <button
                  key={opt.key}
                  className={`ead-sort-btn${sortBy === opt.key ? " active" : ""}`}
                  onClick={() => setSortBy(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {filteredEntries.length === 0 ? (
              <div className="ead-empty">
                <div className="ead-empty-icon">{meta.icon}</div>
                <div className="ead-empty-text">No {meta.entriesLabel.toLowerCase()} yet</div>
              </div>
            ) : (
              filteredEntries.map(renderListItem)
            )}
          </div>
          <div className="ead-inbox-detail">
            {renderDetailPanel()}
          </div>
        </div>
      )}

      {activeTab === "entries" && !isInboxType && renderTableView()}

      {/* Action Bar */}
      {(hasWinners || actionType === "auction" || actionType === "raffle") && (
        <div className="ead-action-bar">
          {actionType === "raffle" && entries.length > 0 && winners.length === 0 && (
            <button className="ead-action-bar-btn primary" onClick={handleDraw}>Draw Winners</button>
          )}
          {(actionType === "auction" || actionType === "challenge" || actionType === "contest") && entries.length > 0 && winners.length === 0 && (
            <button className="ead-action-bar-btn primary" onClick={handleAward}>Award Winners</button>
          )}
          {winners.length > 0 && (
            <button className="ead-action-bar-btn outline" onClick={handleNotify}>Send Notifications</button>
          )}
          <div className="ead-action-bar-spacer" />
          <button
            className="ead-action-bar-btn outline"
            onClick={() => navigate(`/${tenantId}/${eventId}/${itemId}/eventItemDetail`)}
          >
            Back to Item
          </button>
        </div>
      )}
    </div>
  );
};

export default EventActionDetail;
